import React, { useRef, useEffect, useState } from 'react';
import { PhoneIcon } from './Icons';

export default function Sidebar({
  page, setPage,
  historyCount,
  isAuthed, profile,
  onLogout,
  drawer, setDrawer,
  canManageModels,
}) {
  const [expanded, setExpanded] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarWrapRef = useRef(null);

  useEffect(() => {
    document.documentElement.style.setProperty('--sbw', '64px');
    document.documentElement.style.setProperty('--sbg', '16px');
    document.documentElement.style.setProperty('--sbpad', 'calc(var(--sbw) + var(--sbg))');
  }, []);

  useEffect(() => {
    const w = expanded ? 264 : 64;
    document.documentElement.style.setProperty('--sbw', `${w}px`);
    document.documentElement.style.setProperty('--sbpad', `calc(${w}px + var(--sbg))`);
  }, [expanded]);

  useEffect(() => {
    const onDoc = e => { if (avatarOpen && avatarWrapRef.current && !avatarWrapRef.current.contains(e.target)) setAvatarOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setAvatarOpen(false); };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('touchstart', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('touchstart', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [avatarOpen]);

  const nav = [
    {
      k: 'dashboard', label: 'Начало', icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-80">
          <path d="M3 13h8V3H3z" /><path d="M13 21h8V11h-8z" />
          <path d="M13 3h8" /><path d="M13 7h8" /><path d="M3 17h8" /><path d="M3 21h8" />
        </svg>
      )
    },
    {
      k: 'history', label: 'Разговори', icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-80">
          <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" />
        </svg>
      )
    },
  ];

  const RowBtn = ({ active, icon, label, onClick }) => (
    <button
      onClick={onClick}
      className={[
        'mx-2 my-1 flex items-center rounded-xl',
        'transition-colors',
        active ? 'bg-white/10' : 'hover:bg-white/5'
      ].join(' ')}
      title={label}
    >
      <span className="w-10 h-10 shrink-0 grid place-items-center">{icon}</span>
      {expanded && <span className="text-sm pr-3">{label}</span>}
    </button>
  );

  return (
    <>
      {/* MOBILE drawer */}
      {drawer && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setDrawer(false)} />}
      <aside className={[
        'md:hidden fixed inset-y-0 left-0 w-72 z-50',
        'glass border-r border-slate-800/60',
        'transform transition-transform duration-200 ease-out',
        drawer ? 'translate-x-0' : '-translate-x-full',
        'p-4 flex flex-col'
      ].join(' ')}>
        <div className="flex items-center justify-between md:hidden">
          <div className="flex items-center gap-3 px-2 py-3">
            <div className="w-9 h-9 rounded-2xl grid place-items-center bg-violet-600/20 border border-violet-500/30"><PhoneIcon /></div>
            <div><div className="font-bold">Voice Bot</div></div>
          </div>
          <button className="btn btn-ghost" onClick={() => setDrawer(false)}>✕</button>
        </div>

        <nav className="mt-4 space-y-1">
          {nav.map(item => (
            <button key={item.k} onClick={() => { setPage(item.k); setDrawer(false); }} className={`nav-item w-full ${page === item.k ? 'active' : ''}`}>
              {item.icon}<span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="mt-10 p-3 rounded-xl bg-gradient-to-br from-violet-600/20 to-emerald-500/10 border border-violet-500/20">
          <div className="text-sm text-slate-300">Брой разговори</div>
          <div className="text-2xl font-bold">{historyCount ?? '—'}</div>
        </div>

        {isAuthed && (
          <div className="mt-auto pt-4 border-t border-slate-800/60">
            <div className="relative" ref={avatarWrapRef}>
              <button className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition" onClick={() => setAvatarOpen(v => !v)}>
                {profile?.picture ? (
                  <img src={profile.picture} alt={profile?.name || 'Profile'} className="w-9 h-9 rounded-full object-cover border border-violet-500/30 shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full grid place-items-center bg-violet-600/30 border border-violet-500/30 shrink-0">
                    <span className="font-semibold">{(profile?.name || 'U').slice(0, 1).toUpperCase()}</span>
                  </div>
                )}
                <div className="text-left">
                  <div className="text-slate-100 text-sm font-semibold truncate max-w-[140px]">{profile?.name || 'Потребител'}</div>
                  <div className="text-slate-400 text-xs truncate max-w-[140px]">{profile?.email || ''}</div>
                </div>
                <svg className="ml-auto opacity-70" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 15l6-6 6 6" /></svg>
              </button>

              {avatarOpen && (
                <div className="absolute bottom-full mb-2 right-0 w-48 rounded-xl border border-slate-800/60 bg-[#0f1523] shadow-lg z-50 p-1">
                  {canManageModels && (
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-white/5 rounded-lg text-slate-200"
                      onClick={() => { setAvatarOpen(false); setPage('models'); setDrawer(false); }}
                    >
                      Модели
                    </button>
                  )}
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-white/5 rounded-lg text-slate-200"
                    onClick={() => { setAvatarOpen(false); setPage('templates'); setDrawer(false); }}
                  >
                    Темплейти
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-white/5 text-rose-300 rounded-lg"
                    onClick={onLogout}
                  >
                    Излез
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* DESKTOP */}
      <aside
        className="hidden md:flex fixed left-0 top-0 h-screen z-40 glass border-r border-slate-800/60 overflow-hidden"
        style={{ width: expanded ? 264 : 64, transition: 'width 180ms ease' }}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => { setExpanded(false); setAvatarOpen(false); }}
      >
        <div className="flex flex-col w-full min-w-0">
          <div className="flex items-center gap-3 h-14 px-3 border-b border-slate-800/60 shrink-0">
            <div className="w-9 h-9 rounded-2xl grid place-items-center bg-violet-600/20 border border-violet-500/30"><PhoneIcon /></div>
            {expanded && <div className="font-bold truncate">Voice Bot</div>}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto py-2">
            {nav.map(item => (
              <RowBtn
                key={item.k}
                active={page === item.k}
                icon={item.icon}
                label={item.label}
                onClick={() => setPage(item.k)}
              />
            ))}

            {expanded && (
              <div className="mx-3 my-2 p-3 rounded-xl bg-gradient-to-br from-violet-600/20 to-emerald-500/10 border border-violet-500/20">
                <div className="text-sm text-slate-300">Брой разговори</div>
                <div className="text-2xl font-bold">{historyCount ?? '—'}</div>
              </div>
            )}
          </div>

          {isAuthed && (
            <div
              className={`${expanded ? 'p-3' : 'px-0 py-2'} border-t border-slate-800/60 relative`}
              ref={avatarWrapRef}
            >
              <button
                onClick={() => setAvatarOpen(v => !v)}
                title={profile?.name || 'Профил'}
                className={[
                  'flex items-center gap-3 rounded-xl hover:bg-white/5 transition',
                  expanded
                    ? 'w-full px-2 py-2 justify-start'
                    : 'w-11 h-11 p-0 mx-auto justify-center'
                ].join(' ')}
              >
                {profile?.picture ? (
                  <img
                    src={profile.picture}
                    alt={profile?.name || 'Profile'}
                    className="w-9 h-9 rounded-full object-cover border border-violet-500/30 shrink-0"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full grid place-items-center bg-violet-600/30 border border-violet-500/30 shrink-0">
                    <span className="font-semibold">{(profile?.name || 'U').slice(0, 1).toUpperCase()}</span>
                  </div>
                )}

                {expanded && (
                  <div className="min-w-0 text-left">
                    <div className="text-slate-100 text-sm font-semibold truncate">
                      {profile?.name || 'Потребител'}
                    </div>
                    <div className="text-slate-400 text-xs truncate">{profile?.email || ''}</div>
                  </div>
                )}
              </button>

              {avatarOpen && expanded && (
                <div className="absolute bottom-16 left-3 right-3 rounded-xl border border-slate-800/60 bg-[#0f1523] shadow-lg z-50 p-1">
                  {canManageModels && (
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-white/5 rounded-lg text-slate-200"
                      onClick={() => { setAvatarOpen(false); setPage('models'); }}
                    >
                      Модели
                    </button>
                  )}
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-white/5 rounded-lg text-slate-200"
                    onClick={() => { setAvatarOpen(false); setPage('templates'); }}
                  >
                    Темплейти
                  </button>
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-white/5 text-rose-300 rounded-lg"
                    onClick={onLogout}
                  >
                    Излез
                  </button>
                </div>
              )}
            </div>
          )}

        </div>
      </aside>
    </>
  );
}
